import type { Mutable, RunnerItem } from '../../utils';
import type { ComponentClass } from '../Component';
import type { System, SystemClass } from '../System';
import type { NotQueryComponent, OrQueryComponent } from './Query';

/**
 * A helper function to tell a query that this component cannot be used.
 * @param componentClass - component classes to be compared against
 */
export function Not(componentClass: ComponentClass): NotQueryComponent {
  return {
    operator: 'not',
    component: componentClass,
  };
}

/**
 * A helper function to tell a query whether either of the components can be used.
 * @param componentClass - component classes to be compared against
 */
export function Or(...componentClass: ComponentClass[]): OrQueryComponent {
  return {
    operator: 'or',
    components: componentClass,
  };
}

/**
 * Generates a queries for a system. Hooking itself up to the reactive events if required
 * @param systemClass - Class to get queries from
 * @param system - instance of the class to create queries object
 */
export function generateQueries(
  systemClass: SystemClass,
  system: Mutable<System>,
): void {
  if (systemClass.Queries) {
    for (const queryName in systemClass.Queries) {
      const queryConfig = systemClass.Queries[queryName];
      const queryComponents = queryConfig.components;

      if (!queryComponents || queryComponents.length === 0) {
        throw new Error("'components' attribute can't be empty in a query");
      }

      if (!system.scene) {
        throw new Error('system.scene is undefined');
      }

      const query = system.scene.queryManager.getQuery(
        queryComponents,
        queryName,
      );

      if (!system.queries) system.queries = {};
      system.queries[queryName] = query;

      if (queryConfig.modified) {
        query.reactive = true;
        query.runners.modified.add(system as RunnerItem<'modifiedQuery'>);
      }
      if (queryConfig.added) {
        query.reactive = true;
        query.runners.added.add(system as RunnerItem<'addedToQuery'>);
      }
      if (queryConfig.removed) {
        query.reactive = true;
        query.runners.removed.add(system as RunnerItem<'removedFromQuery'>);
      }
    }
  }
}
